/**
 * Serato Crate + Rekordbox XML import block — a small drop-in section
 * inside the Library tab. Handles:
 *   1. Picking a .m3u / .m3u8 (Serato) OR .xml (Rekordbox) file
 *   2. Parsing tracks + metadata (title, artist, BPM, key, hot cues)
 *   3. Persisting parsed crates in localStorage across sessions
 *   4. "Match Audio" — user selects local audio files; we join by filename
 *      and pass matched files upstream via `onLocalFiles` so they show up
 *      in the regular Local library and are drag-loadable into any deck.
 *
 * Kept intentionally lean — everything lives client-side, no backend calls.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderPlus, Layers, Trash2, ChevronDown, ChevronRight, Zap } from "lucide-react";
import { parseM3U, loadStoredCrates, addCrate, removeCrate, updateCrate } from "@/lib/serato/parseM3U";
import { parseRekordboxXML } from "@/lib/rekordbox/parseXML";
import { parseTraktorNML } from "@/lib/traktor/parseNML";
import { parseVirtualDJXML } from "@/lib/virtualdj/parseDatabaseXML";
import { readSeratoHotCues } from "@/lib/serato/id3";

export default function SeratoImport({ onLocalFiles }) {
  const [crates, setCrates] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [pulseId, setPulseId] = useState(null);
  const importInputRef = useRef(null);
  const matchInputRef = useRef(null);
  const [matchTargetId, setMatchTargetId] = useState(null);
  const crateRefs = useRef({});

  useEffect(() => { setCrates(loadStoredCrates()); }, []);

  // Whenever a fresh import lands, scroll to + pulse the crate row for 3s
  // so DJs immediately see where the tracks went + the "Match audio" CTA.
  useEffect(() => {
    if (!pulseId) return;
    const el = crateRefs.current[pulseId];
    if (el && el.scrollIntoView) {
      try { el.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch {}
    }
    const t = setTimeout(() => setPulseId(null), 3200);
    return () => clearTimeout(t);
  }, [pulseId]);

  const onPickCrate = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isNml = /\.nml$/i.test(file.name);
    const isXml = /\.xml$/i.test(file.name);
    try {
      const text = await file.text();
      if (isNml || (isXml && /<NML[\s>]/i.test(text.slice(0, 512)))) {
        // Traktor collection.nml — sometimes users rename it to .xml but the
        // <NML> root gives it away in the first 512 bytes.
        const parsed = parseTraktorNML(text);
        if (!parsed.crates.length) {
          toast.error("No tracks found. Copy your collection.nml from ~/Documents/Native Instruments/Traktor <version>/");
          return;
        }
        let next = loadStoredCrates();
        for (const c of parsed.crates) next = addCrate(c.name, c.tracks);
        setCrates(next);
        setExpandedId(next[0]?.id || null);
        setPulseId(next[0]?.id || null);
        const totalTracks = parsed.crates.reduce((n, c) => n + c.tracks.length, 0);
        const totalHotCues = parsed.crates.reduce(
          (n, c) => n + c.tracks.reduce((m, t) => m + (t.hot_cues?.length || 0), 0),
          0,
        );
        const withCamelot = parsed.crates.reduce(
          (n, c) => n + c.tracks.reduce((m, t) => m + (t.key ? 1 : 0), 0),
          0,
        );
        // First-time Traktor reward — richer message once, then downgrade to
        // the standard "imported" toast on subsequent NML drops.
        const seenTraktor = (() => { try { return localStorage.getItem("nd-traktor-seen") === "1"; } catch { return false; } })();
        try { localStorage.setItem("nd-traktor-seen", "1"); } catch { /* noop */ }
        if (!seenTraktor) {
          toast.success(`Now on Traktor — welcome`, {
            description: `${parsed.crates.length} playlist${parsed.crates.length === 1 ? "" : "s"} · ${totalTracks} tracks · ${totalHotCues} hot cue${totalHotCues === 1 ? "" : "s"} preserved · ${withCamelot} keys in Camelot`,
            duration: 7000,
          });
        } else {
          toast.success(`Imported ${parsed.crates.length} Traktor playlist${parsed.crates.length > 1 ? "s" : ""} · ${totalTracks} tracks · ${totalHotCues} hot cues`);
        }
        return;
      }
      if (isXml) {
        // XML files could be Rekordbox (`<DJ_PLAYLISTS>`) or VirtualDJ
        // (`<VirtualDJ_Database>`). Sniff the first few hundred bytes.
        const preview = text.slice(0, 512);
        if (/<VirtualDJ_Database[\s>]/i.test(preview)) {
          const parsed = parseVirtualDJXML(text);
          if (!parsed.crates.length) {
            toast.error("No tracks found. Make sure you exported the full VirtualDJ database.xml.");
            return;
          }
          let next = loadStoredCrates();
          for (const c of parsed.crates) next = addCrate(c.name, c.tracks);
          setCrates(next);
          setExpandedId(next[0]?.id || null);
          setPulseId(next[0]?.id || null);
          const totalTracks = parsed.crates.reduce((n, c) => n + c.tracks.length, 0);
          const totalHotCues = parsed.crates.reduce((n, c) => n + c.tracks.reduce((m, t) => m + (t.hot_cues?.length || 0), 0), 0);
          const seenVDJ = (() => { try { return localStorage.getItem("nd-virtualdj-seen") === "1"; } catch { return false; } })();
          try { localStorage.setItem("nd-virtualdj-seen", "1"); } catch { /* noop */ }
          if (!seenVDJ) {
            toast.success(`Now on VirtualDJ — welcome`, {
              description: `${totalTracks} tracks · ${totalHotCues} hot cues preserved · POI cues → Camelot mapped`,
              duration: 7000,
            });
          } else {
            toast.success(`Imported VirtualDJ library · ${totalTracks} tracks · ${totalHotCues} hot cues`);
          }
          return;
        }
        // Rekordbox — one XML export can yield multiple crates (one per
        // playlist), each with tracks that already carry POSITION_MARK
        // hot-cue metadata.
        const parsed = parseRekordboxXML(text);
        if (!parsed.crates.length) {
          toast.error("No tracks found. Re-export from Rekordbox → File → Export Collection in xml format.");
          return;
        }
        let next = loadStoredCrates();
        for (const c of parsed.crates) next = addCrate(c.name, c.tracks);
        setCrates(next);
        setExpandedId(next[0]?.id || null);
        setPulseId(next[0]?.id || null);
        const totalTracks = parsed.crates.reduce((n, c) => n + c.tracks.length, 0);
        toast.success(`Imported ${parsed.crates.length} Rekordbox crate${parsed.crates.length > 1 ? "s" : ""} · ${totalTracks} tracks · hot cues preserved`);
        return;
      }
      // Serato M3U/M3U8 branch
      const parsed = parseM3U(text);
      if (!parsed.count) {
        toast.error("No tracks found. Export as .m3u8 from Serato (right-click crate → Export → Playlist as .m3u8).");
        return;
      }
      const name = parsed.crate_name && parsed.crate_name !== "Imported crate"
        ? parsed.crate_name
        : file.name.replace(/\.[^.]+$/, "");
      const next = addCrate(name, parsed.tracks);
      setCrates(next);
      setExpandedId(next[0].id);
      setPulseId(next[0].id);
      toast.success(`Imported "${name}" · ${parsed.count} tracks`);
    } catch (err) {
      const msg = err?.message?.includes("Malformed XML") || err?.message?.includes("<NML>") || err?.message?.includes("VirtualDJ")
        ? err.message
        : "Couldn't read that file — needs .m3u/.m3u8 (Serato), .xml (Rekordbox or VirtualDJ), or .nml (Traktor).";
      toast.error(msg);
    }
  };

  const onPickMatchFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length || !matchTargetId) return;
    const crate = crates.find(c => c.id === matchTargetId);
    if (!crate) return;
    // Build a lookup of crate filenames (basename, lowercase) so match is fast.
    const wanted = new Map();
    crate.tracks.forEach(t => {
      const key = (t.filename || "").toLowerCase();
      if (key) wanted.set(key, t);
    });
    const matched = picked.filter(f => wanted.has(f.name.toLowerCase()));
    if (!matched.length) {
      toast.error(`No files matched crate filenames. Serato paths use exact basename (e.g. "Track.mp3").`);
      return;
    }
    // Convert to a FileList-like array and hand to the parent
    onLocalFiles({ target: { files: matched } });

    // Serato-tagged MP3s carry hot cues inside ID3v2 GEOB "Serato Markers2".
    // For each matched file, try to read them; if found, enrich the crate.
    // Silent on failure — non-Serato files just return [].
    let cueEnriched = 0;
    const enrichedTracks = crate.tracks.slice();
    await Promise.all(matched.map(async (f) => {
      if (!/\.mp3$/i.test(f.name)) return;
      const cues = await readSeratoHotCues(f);
      if (!cues.length) return;
      const idx = enrichedTracks.findIndex(t => (t.filename || "").toLowerCase() === f.name.toLowerCase());
      if (idx >= 0) {
        enrichedTracks[idx] = { ...enrichedTracks[idx], hot_cues: cues };
        cueEnriched++;
      }
    }));
    if (cueEnriched > 0) {
      const next = updateCrate(matchTargetId, { tracks: enrichedTracks });
      setCrates(next);
      toast.success(`Loaded ${matched.length} of ${crate.tracks.length} into library · ${cueEnriched} tracks got Serato hot cues`);
    } else {
      toast.success(`Loaded ${matched.length} of ${crate.tracks.length} into library · unmatched shown as metadata-only`);
    }
    setMatchTargetId(null);
  };

  const onDelete = (id) => {
    if (!window.confirm("Remove this crate from your imports?")) return;
    setCrates(removeCrate(id));
  };

  const fmtBpm = (b) => b ? `${(Math.round(b * 10) / 10).toFixed(1)}` : null;

  return (
    <div className="pt-2" data-testid="serato-import-block">
      <button
        data-testid="serato-import-button"
        onClick={() => importInputRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-[#FF2A6D]/40 hover:border-[#FF2A6D] hover:bg-[#FF2A6D]/5 text-[#FF2A6D] font-black uppercase tracking-widest text-xs transition-colors"
      >
        <FolderPlus className="w-4 h-4"/> Import Serato .m3u8 / Rekordbox .xml / Traktor .nml / VirtualDJ .xml
      </button>
      <input
        ref={importInputRef}
        data-testid="serato-import-input"
        type="file"
        accept=".m3u,.m3u8,.xml,.nml,audio/mpegurl,audio/x-mpegurl,application/vnd.apple.mpegurl,application/xml,text/xml"
        onChange={onPickCrate}
        className="hidden"
      />
      <input
        ref={matchInputRef}
        data-testid="serato-match-input"
        type="file"
        accept="audio/*"
        multiple
        onChange={onPickMatchFiles}
        className="hidden"
      />

      {crates.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="serato-crate-list">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF2A6D]">
            // Imported crates ({crates.length})
          </div>
          {crates.map(c => {
            const withHotCues = c.tracks.reduce((n, t) => n + (t.hot_cues?.length ? 1 : 0), 0);
            return (
            <div key={c.id} data-testid={`serato-crate-${c.id}`}
                 className="rounded-lg border border-[#FF2A6D]/30 bg-[#0B0E14]">
              <div className="flex items-center gap-2 px-3 py-2">
                <button type="button"
                        data-testid={`serato-crate-expand-${c.id}`}
                        onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                        className="text-[#94A3B8] hover:text-white transition">
                  {expandedId === c.id ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                </button>
                <Layers className="w-3.5 h-3.5 text-[#FF2A6D] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-black truncate">{c.name}</div>
                  <div className="text-[10px] text-[#94A3B8] font-mono uppercase tracking-widest">
                    {c.tracks.length} tracks
                    {withHotCues > 0 && <span className="text-[#00F0FF]"> · {withHotCues} with hot cues</span>}
                  </div>
                </div>
                <button type="button"
                        data-testid={`serato-crate-match-${c.id}`}
                        onClick={() => { setMatchTargetId(c.id); matchInputRef.current?.click(); }}
                        title="Pick your audio files to match this crate by filename"
                        className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:text-[#33F3FF] flex items-center gap-1 px-2 py-1 rounded border border-[#00F0FF]/40 hover:border-[#00F0FF] transition">
                  <Zap className="w-3 h-3"/> Match audio
                </button>
                <button type="button"
                        data-testid={`serato-crate-delete-${c.id}`}
                        onClick={() => onDelete(c.id)}
                        className="text-[#94A3B8] hover:text-[#FF2A6D] transition p-1">
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              </div>
              {expandedId === c.id && (
                <div className="border-t border-[#1E2638] max-h-56 overflow-y-auto" data-testid={`serato-crate-tracks-${c.id}`}>
                  {c.tracks.slice(0, 200).map((t, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-[#00F0FF]/5">
                      <span className="text-[#94A3B8] font-mono w-6 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white truncate">{t.title || t.filename || "Untitled"}</div>
                        <div className="text-[10px] text-[#94A3B8] truncate">{t.artist || "Unknown artist"}</div>
                      </div>
                      {fmtBpm(t.bpm) && (
                        <span className="text-[10px] font-mono text-[#FFC107]">{fmtBpm(t.bpm)}</span>
                      )}
                      {t.key && <span className="text-[10px] font-mono text-[#00F0FF]">{t.key}</span>}
                    </div>
                  ))}
                  {c.tracks.length > 200 && (
                    <div className="px-3 py-2 text-[10px] text-[#94A3B8] italic border-t border-[#1E2638]">
                      + {c.tracks.length - 200} more tracks (only first 200 shown)
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
